import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy, inject, output } from '@angular/core';

export type ToolbarDensity = 0 | 1 | 2;

export interface ToolbarMeasurements {
  containerWidth: number;
  tabsWidth: number;
  filtersWidth: number;
  hostGap: number;
}

/**
 * Steps the toolbar down through three densities based on intrinsic content widths:
 *   0 — tabs and filters fit on one row
 *   1 — tabs move to their own row; filters occupy one row below
 *   2 — filters no longer fit one row and split into two
 *
 * `filtersWidth` is the single-row natural width, so the result never depends on the
 * density currently applied — measuring stays free of the feedback loop that would
 * otherwise thrash between states.
 */
export function computeToolbarDensity({ containerWidth, tabsWidth, filtersWidth, hostGap }: ToolbarMeasurements): ToolbarDensity {
  const tabsBlock = tabsWidth > 0 ? tabsWidth + hostGap : 0;
  if (tabsBlock + filtersWidth <= containerWidth) {
    return 0;
  }
  if (filtersWidth <= containerWidth) {
    return 1;
  }
  return 2;
}

@Directive({
  selector: '[edhResponsiveToolbar]',
  standalone: true,
})
export class ResponsiveToolbarDirective implements AfterViewInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);

  readonly densityChange = output<ToolbarDensity>();

  private resizeObserver?: ResizeObserver;
  private frameHandle = 0;
  private lastDensity?: ToolbarDensity;

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.host.nativeElement);
      this.observeContent();
      this.measure();
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.frameHandle) {
      cancelAnimationFrame(this.frameHandle);
    }
  }

  // Observing the tabs and each cluster catches content-driven size changes (a tab
  // pill appearing, the export button toggling) that a container-only observer misses.
  private observeContent(): void {
    const targets = this.host.nativeElement.querySelectorAll<HTMLElement>('[data-toolbar-tabs], [data-toolbar-cluster]');
    targets.forEach((target) => this.resizeObserver?.observe(target));
  }

  private scheduleMeasure(): void {
    if (typeof requestAnimationFrame !== 'function') {
      this.measure();
      return;
    }
    if (this.frameHandle) {
      return;
    }
    this.frameHandle = requestAnimationFrame(() => {
      this.frameHandle = 0;
      this.measure();
    });
  }

  private measure(): void {
    const host = this.host.nativeElement;
    const filters = host.querySelector<HTMLElement>('[data-toolbar-filters]');
    if (!filters) {
      return;
    }

    const clusters = Array.from(filters.querySelectorAll<HTMLElement>('[data-toolbar-cluster]'));
    if (!clusters.length) {
      return;
    }

    this.observeContent();

    const tabs = host.querySelector<HTMLElement>('[data-toolbar-tabs]');
    // The gap lives on the clusters' flex parent (the filters form), not the host element
    // the [data-toolbar-filters] hook sits on — reading it off the host would miss every gap.
    const clusterContainer = clusters[0].parentElement;
    const clusterGap = clusterContainer ? parseFloat(getComputedStyle(clusterContainer).columnGap) || 0 : 0;
    const filtersWidth = clusters.reduce((sum, cluster) => sum + cluster.scrollWidth, 0) + clusterGap * (clusters.length - 1);

    const density = computeToolbarDensity({
      containerWidth: host.clientWidth,
      tabsWidth: tabs?.offsetWidth ?? 0,
      filtersWidth,
      hostGap: parseFloat(getComputedStyle(host).columnGap) || 0,
    });

    if (density === this.lastDensity) {
      return;
    }
    this.lastDensity = density;
    this.zone.run(() => this.densityChange.emit(density));
  }
}
